import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { getConfig } from '../config';
import { getCart, getCountries } from '../api';
import { store } from '../store';
import { navigateToView } from '../navigation';
import { t } from '../i18n';
import { incompatibleDelivery, normalizeDeliveryCountry, type DeliveryRestriction } from '../utils/deliveryEligibility';

export function DeliveryNotice({ rules, country }: { rules: DeliveryRestriction[]; country: string }) {
  const blocked = incompatibleDelivery(rules, country);
  if (!blocked.length) return null;
  return <div class="alert alert-warning" role="alert">
    <p>{t('delivery.blocked')}</p>
    <ul>{blocked.map(rule => <li key={rule.productid}>{rule.title} — {t('delivery.availableTo')} {(rule.regions ?? []).join(', ')}</li>)}</ul>
    <button type="button" class="btn btn-outline-primary" onClick={() => navigateToView('cart')}>{t('delivery.backToCart')}</button>
  </div>;
}

/** Read-only preflight. It never recalculates, removes items, submits forms, or uses IP as destination. */
export function DeliveryGate({ children, accountCountry = false }: {
  children: (country: string, rules: DeliveryRestriction[]) => ComponentChildren;
  accountCountry?: boolean;
}) {
  const config = getConfig();
  const storageKey = `sw.deliveryCountry:${config.siteKey}`;
  const [country, setCountry] = useState(() => {
    if (accountCountry) return normalizeDeliveryCountry((store.getAuth() as { s_country?: string } | null)?.s_country);
    try { return normalizeDeliveryCountry(sessionStorage.getItem(storageKey)); } catch { return ''; }
  });
  const [rules, setRules] = useState<DeliveryRestriction[] | null>(null);
  const destination = accountCountry
    ? normalizeDeliveryCountry((store.getAuth() as { s_country?: string } | null)?.s_country)
    : country;
  const [countries, setCountries] = useState<Record<string, string>>({});
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setError(false);
    setRules(null);
    Promise.all([getCart(config, { skipCache: true }), accountCountry ? Promise.resolve({ status: 200, result: {} }) : getCountries(config)]).then(([cart, list]) => {
      if (cancelled) return;
      if (cart.status !== 200 || !Array.isArray(cart.result?.delivery_restrictions)) {
        setError(true);
        return;
      }
      const nextRules = cart.result.delivery_restrictions;
      if (!accountCountry && nextRules.length && (list.status !== 200 || !list.result || !Object.keys(list.result).length)) {
        setError(true);
        return;
      }
      setCountries(list.result ?? {});
      setRules(nextRules);
    }).catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [config.apiUrl, config.siteKey, retry, accountCountry]);

  if (error) return <div class="alert alert-warning" role="alert">
    <p>{t('delivery.checkFailed')}</p>
    <button type="button" class="btn btn-primary me-2" onClick={() => setRetry(value => value + 1)}>{t('delivery.retry')}</button>
    <button type="button" class="btn btn-outline-primary" onClick={() => navigateToView('cart')}>{t('delivery.backToCart')}</button>
  </div>;
  if (rules === null) return <p role="status">{t('delivery.checking')}</p>;
  if (!rules.length || (destination && !incompatibleDelivery(rules, destination).length)) return <>{children(destination, rules)}</>;
  return <section class="sw-surface-card p-3">
    <h3 class="h5">{t('delivery.title')}</h3>
    <p>{t('delivery.beforeSignup')}</p>
    {accountCountry ? <>
      <DeliveryNotice rules={rules} country={destination} />
      <button type="button" class="btn btn-outline-primary" onClick={() => navigateToView('account')}>{t('delivery.updateAddress')}</button>
    </> : <>
      <label class="form-label" for="sw-delivery-country">{t('delivery.country')}</label>
      <select id="sw-delivery-country" class="form-select mb-3" value={country} onChange={e => {
        const value = normalizeDeliveryCountry(e.currentTarget.value);
        setCountry(value);
        try { sessionStorage.setItem(storageKey, value); } catch { /* optional preference only */ }
      }}>
        <option value="">{t('delivery.choose')}</option>
        {Object.entries(countries).map(([code, label]) => <option value={code} key={code}>{label}</option>)}
      </select>
      {country ? <DeliveryNotice rules={rules} country={country} /> : <ul>{rules.map(rule =>
        <li key={rule.productid}>{rule.title} — {t('delivery.availableTo')} {(rule.regions ?? []).join(', ')}</li>
      )}</ul>}
    </>}
  </section>;
}
